/**
 * Utilitaires d'export - Version fonctionnelle
 * 
 * Ce fichier contient les fonctions d'export principales pour assurer
 * la compatibilité avec le code existant.
 */

import * as XLSX from 'xlsx';
import api from '../components/api';
import { exportToPDF } from './pdfExport';

/**
 * Formate un montant en FCFA avec des espaces
 */
const formatMontantFCFA = (montant) => {
  if (!montant && montant !== 0) return '0 FCFA';
  return montant.toLocaleString('fr-FR').replace(/\s/g, ' ') + ' FCFA';
};

/**
 * Export simple vers Excel
 */
export const exportToExcel = (data, filename = 'export', sheetName = 'Données') => {
  try {
    if (!data || !Array.isArray(data) || data.length === 0) {
      throw new Error('Aucune donnée à exporter');
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    
    // Ajuster la largeur des colonnes
    const colWidths = Object.keys(data[0]).map(key => ({
      wch: Math.max(key.length, 15)
    }));
    worksheet['!cols'] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    
    XLSX.writeFile(workbook, `${filename}.xlsx`);
    
    return { 
      success: true, 
      message: `Export Excel réussi: ${data.length} lignes exportées` 
    };
  } catch (error) {
    console.error('Erreur export Excel:', error);
    return { 
      success: false, 
      message: error.message || 'Erreur lors de l\'export Excel' 
    };
  }
};

/**
 * Export simple vers CSV avec BOM UTF-8
 */
export const exportToCSV = (data, filename = 'export', separator = ';') => {
  try {
    if (!data || !Array.isArray(data) || data.length === 0) {
      throw new Error('Aucune donnée à exporter');
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(separator),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          const stringValue = value !== null && value !== undefined ? String(value) : '';
          return stringValue.includes(separator) || stringValue.includes('"') || stringValue.includes('\n') 
            ? `"${stringValue.replace(/"/g, '""')}"` 
            : stringValue;
        }).join(separator)
      )
    ].join('\n');

    // Ajouter BOM UTF-8 pour Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return { 
      success: true, 
      message: `Export CSV réussi: ${data.length} lignes exportées` 
    };
  } catch (error) {
    console.error('Erreur export CSV:', error);
    return { 
      success: false, 
      message: error.message || 'Erreur lors de l\'export CSV' 
    };
  }
};

/**
 * Export simple vers PDF - utilise le module pdfExport
 */
export const exportToPDFManuel = (data, filename = 'export', options = {}) => {
  return exportToPDF(data, filename, options);
};

/**
 * Formatage des données d'étudiants
 */
export const formatEtudiantsForExport = (etudiants) => {
  if (!Array.isArray(etudiants)) return [];
  
  return etudiants.map(etudiant => ({
    'Matricule': etudiant.matricule || '',
    'Nom': etudiant.nom || '',
    'Prénom': etudiant.prenom || '',
    'Contact': etudiant.contact || '',
    'Email': etudiant.email || '',
    'Adresse': etudiant.adresse || '',
    'Filière': etudiant.get_filiere || '',
    'Niveau': etudiant.get_niveau || '',
    'Classe': etudiant.get_classe || '',
    'Année': etudiant.get_year || ''
  }));
};

/**
 * Formatage des données d'inscriptions
 */
export const formatInscriptionsForExport = (inscriptions) => {
  if (!Array.isArray(inscriptions)) return [];
  
  return inscriptions.map(inscription => ({
    'Nom': inscription.eleve_nom || '',
    'Prénom': inscription.eleve_prenom || '',
    'Classe': inscription.classe_nom || '',
    'Filière': inscription.filiere_nom || '',
    'Niveau': inscription.niveau_nom || '',
    'Année': inscription.annee_libelle || '',
    'Montant Payé': formatMontantFCFA(inscription.montant_paye),
    'Montant Impayé': formatMontantFCFA(inscription.montant_impaye),
    'Statut': inscription.is_annuler ? 'Annulée' : (inscription.status ? 'Actif' : 'En attente')
  }));
};

/**
 * Export des étudiants
 */
export const exportEtudiants = async (etudiants, format, filename, filters = {}) => {
  try {
    // console.log('📊 Export étudiants:', { format, count: etudiants?.length });
    
    let dataToExport = etudiants;
    
    // Si pas de données fournies, récupérer depuis l'API
    if (!dataToExport || dataToExport.length === 0) {
      dataToExport = await fetchAllEtudiantsForExport(filters);
    }
    
    if (!dataToExport || dataToExport.length === 0) {
      return { success: false, message: 'Aucune donnée à exporter' };
    }
    
    // Formater les données
    const formattedData = formatEtudiantsForExport(dataToExport);
    
    // Exporter selon le format
    switch (format.toLowerCase()) {
      case 'excel':
        return exportToExcel(formattedData, filename, 'Étudiants');
      case 'csv':
        return exportToCSV(formattedData, filename);
      case 'pdf':
        return await exportToPDFManuel(formattedData, filename, { title: 'LISTE DES ÉTUDIANTS' });
      default:
        throw new Error(`Format non supporté: ${format}`);
    }
  } catch (error) {
    console.error('Erreur export étudiants:', error);
    return { success: false, message: error.message };
  }
};

/**
 * Export des inscriptions
 */
export const exportInscriptions = async (inscriptions, format, filename, filters = {}) => {
  try {
    // console.log('📊 Export inscriptions:', { format, count: inscriptions?.length });
    
    let dataToExport = inscriptions;
    
    // Si pas de données fournies, récupérer depuis l'API
    if (!dataToExport || dataToExport.length === 0) {
      dataToExport = await fetchAllInscriptionsForExport(filters);
    }
    
    if (!dataToExport || dataToExport.length === 0) {
      return { success: false, message: 'Aucune donnée à exporter' };
    }
    
    // Formater les données
    const formattedData = formatInscriptionsForExport(dataToExport);
    
    // Exporter selon le format
    switch (format.toLowerCase()) {
      case 'excel':
        return exportToExcel(formattedData, filename, 'Inscriptions');
      case 'csv':
        return exportToCSV(formattedData, filename);
      case 'pdf':
        return await exportToPDFManuel(formattedData, filename, { title: 'LISTE DES INSCRIPTIONS' });
      default:
        throw new Error(`Format non supporté: ${format}`);
    }
  } catch (error) {
    console.error('Erreur export inscriptions:', error);
    return { success: false, message: error.message };
  }
};

/**
 * Récupération des données d'étudiants
 */
export const fetchAllEtudiantsForExport = async (filters = {}) => {
  try {
    const params = {
      page_size: 10000,
      ...filters
    };

    const response = await api.get('/etudiant/liste-etudiants/', { params });
    
    if (response.data && response.data.data) {
      return response.data.data;
    } else if (response.data && response.data.results) {
      return response.data.results;
    } else if (Array.isArray(response.data)) {
      return response.data;
    } else {
      throw new Error('Format de réponse inattendu');
    }
  } catch (error) {
    console.error('Erreur récupération étudiants:', error);
    throw new Error('Impossible de récupérer les données des étudiants');
  }
};

/**
 * Récupération des données d'inscriptions
 */
export const fetchAllInscriptionsForExport = async (filters = {}) => {
  try {
    const params = {
      page_size: 10000,
      ...filters
    };

    const response = await api.get('/inscription/liste-inscriptions/', { params });
    
    if (response.data && response.data.status === 'success' && response.data.data) {
      return response.data.data;
    } else if (response.data && response.data.results) {
      return response.data.results;
    } else if (Array.isArray(response.data)) {
      return response.data;
    } else {
      throw new Error('Format de réponse inattendu');
    }
  } catch (error) {
    console.error('Erreur récupération inscriptions:', error);
    throw new Error('Impossible de récupérer les données des inscriptions');
  }
}; 